import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function AssignToArtist() {
    const [artists, setArtists] = useState([]);
    const [products, setProducts] = useState([]);
    const [artistId, setArtistId] = useState("");
    const [selected, setSelected] = useState([]);
    const [userSearch, setUserSearch] = useState("");
    const [users, setUsers] = useState([]);
    const [linking, setLinking] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [pluginQuery, setPluginQuery] = useState("");

    const loadArtists = () => api.get("/admin/artists").then(({ data }) => setArtists(data || []));
    useEffect(() => { loadArtists(); }, []);
    useEffect(() => { api.get("/admin/products").then(({ data }) => setProducts(data || [])); }, []);
    useEffect(() => {
        if (!userSearch) { setUsers([]); return; }
        api.get("/admin/users", { params: { q: userSearch } }).then(({ data }) => setUsers(data || []));
    }, [userSearch]);

    const artist = artists.find((a) => a.id === artistId);
    const linked = artist?.linked_account;

    useEffect(() => { setSelected([]); }, [artistId]);

    const filteredProducts = useMemo(() => {
        const q = pluginQuery.trim().toLowerCase();
        return q ? products.filter((p) => p.name.toLowerCase().includes(q)) : products;
    }, [products, pluginQuery]);

    const toggle = (pid) => {
        setSelected((s) => (s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]));
    };

    const linkAccount = async (userId) => {
        setLinking(true);
        try {
            await api.post(`/admin/artists/${artistId}/link-account`, { user_id: userId });
            toast.success("Account linked");
            setUserSearch(""); setUsers([]);
            await loadArtists();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setLinking(false);
        }
    };

    const unlink = async () => {
        setLinking(true);
        try {
            await api.post(`/admin/artists/${artistId}/link-account`, { user_id: null });
            toast.success("Account unlinked");
            await loadArtists();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setLinking(false);
        }
    };

    const assign = async () => {
        if (!artistId) { toast.error("Select an artist"); return; }
        if (!linked) { toast.error("Link an account to this artist first"); return; }
        if (selected.length === 0) { toast.error("Select at least one plugin"); return; }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/admin/artists/${artistId}/assign-plugins`, { product_ids: selected });
            const a = (data.assigned || []).length;
            const s = (data.skipped || []).length;
            toast.success(`${a} plugin(s) assigned${s ? ` · ${s} already owned (skipped)` : ""}`);
            setSelected([]);
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <DashboardLayout role="admin">
            <div className="mb-8" data-testid="assign-artist-page">
                <div className="label-mono mb-3">/ Assign to artist</div>
                <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Assign plugins to an artist</h1>
                <p className="text-avx-muted mt-2 max-w-2xl">
                    Grant plugins (free / endorsement) directly to an artist's linked account. They'll appear in that account's plugins, licenses and downloads.
                </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
                {/* Artist + account */}
                <div className="panel p-6 space-y-4">
                    <div className="label-mono">/ Artist</div>
                    <Select value={artistId} onValueChange={setArtistId}>
                        <SelectTrigger className="bg-avx-bg border-avx-line h-11" data-testid="assign-artist-select">
                            <SelectValue placeholder="Select artist" />
                        </SelectTrigger>
                        <SelectContent>
                            {artists.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                        </SelectContent>
                    </Select>

                    {artist && (
                        <div className="border border-avx-line rounded-sm p-4" data-testid="assign-artist-account">
                            <div className="label-mono mb-2">/ Linked account</div>
                            {linked ? (
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <div className="text-sm font-heading font-bold">{linked.name}</div>
                                        <div className="font-mono text-xs text-avx-muted">{linked.email}</div>
                                    </div>
                                    <button onClick={unlink} disabled={linking} className="btn-secondary px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.18em] rounded-sm" data-testid="assign-artist-unlink">
                                        Unlink
                                    </button>
                                </div>
                            ) : (
                                <div>
                                    <p className="text-sm text-avx-muted mb-3">No account linked. Search a user to link.</p>
                                    <Input placeholder="Search user by name or email…" value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="bg-avx-bg border-avx-line h-11" data-testid="assign-artist-user-search" />
                                    {users.length > 0 && (
                                        <div className="mt-2 max-h-56 overflow-y-auto border border-avx-line rounded-sm divide-y divide-avx-line">
                                            {users.map((u) => (
                                                <button key={u.id} onClick={() => linkAccount(u.id)} disabled={linking}
                                                    className="w-full text-left px-3 py-2 hover:bg-avx-surface/60 transition-colors" data-testid={`assign-artist-link-${u.id}`}>
                                                    <div className="text-sm">{u.name} <span className="label-mono ml-2">{u.role}</span></div>
                                                    <div className="font-mono text-xs text-avx-muted">{u.email}</div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Plugins */}
                <div className="panel p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="label-mono">/ Plugins ({selected.length} selected)</div>
                        {selected.length > 0 && (
                            <button onClick={() => setSelected([])} className="text-[10px] font-mono uppercase tracking-[0.18em] text-avx-muted hover:text-avx-blue">Clear</button>
                        )}
                    </div>
                    <Input placeholder="Filter plugins…" value={pluginQuery} onChange={(e) => setPluginQuery(e.target.value)} className="bg-avx-bg border-avx-line h-11" data-testid="assign-artist-plugin-filter" />
                    <div className="max-h-80 overflow-y-auto border border-avx-line rounded-sm divide-y divide-avx-line">
                        {filteredProducts.length === 0 ? (
                            <div className="p-4 text-sm text-avx-muted">No plugins.</div>
                        ) : (
                            filteredProducts.map((p) => (
                                <label key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-avx-surface/60 transition-colors cursor-pointer" data-testid={`assign-artist-plugin-${p.id}`}>
                                    <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} className="accent-avx-blue" />
                                    {p.image_url && <img src={p.image_url} alt="" className="w-8 h-8 object-cover rounded-sm border border-avx-line" />}
                                    <span className="text-sm flex-1">{p.name}</span>
                                </label>
                            ))
                        )}
                    </div>
                    <button onClick={assign} disabled={submitting || !linked || selected.length === 0}
                        className="btn-primary w-full py-3 text-xs font-mono uppercase tracking-[0.2em] rounded-sm disabled:opacity-50" data-testid="assign-artist-submit">
                        {submitting ? "Assigning…" : `Assign ${selected.length} plugin(s) (free)`}
                    </button>
                    {!linked && artist && (
                        <p className="text-[11px] text-avx-danger font-mono">Link an account above to enable assignment.</p>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
}
